import React, { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import "../../i18/config";
import { cancelWorkflowExecution, getWorkflowExecutions } from "../../api";
import * as route from "../../api/routes";
import BreadCrumb from "../../components/Common/BreadCrumb";
import Loader from "../../components/Loader";
import { ExecutionStatus, WorkflowExecution } from "./types";
import { ExecutionStatusBadge } from "./StatusBadge";
import { formatWorkflowDate } from "./format";
import ListCard from "./ui/ListCard";
import WorkflowTable from "./ui/WorkflowTable";
import RowActions, { actionsColumn } from "./ui/RowActions";

const PER_PAGE = 30;
const ACTIVE_REFRESH_MS = 5000;

const duration = (e: WorkflowExecution): string => {
    if (!e.startedAt) {
        return '—';
    }
    const end = e.finishedAt ? new Date(e.finishedAt).getTime() : Date.now();
    const seconds = Math.max(0, Math.round((end - new Date(e.startedAt).getTime()) / 1000));

    return seconds < 60 ? seconds + ' s' : seconds < 3600 ? Math.round(seconds / 60) + ' min' : Math.round(seconds / 3600) + ' h';
};

/**
 * Automation → Runs: the history of every scenario, or of one
 * (?scenario=uuid), and the active flows (?active=1): runs still going or
 * waiting for something.
 */
const RunList = () => {
    const { t } = useTranslation(['workflow', 'menu', 'common']);
    const location = useLocation();
    const navigate = useNavigate();
    const params = new URLSearchParams(location.search);
    const scenario = params.get('scenario') || '';

    const [active, setActive] = useState(params.get('active') === '1');
    const [status, setStatus] = useState<string>('');
    const [page, setPage] = useState(1);
    const [items, setItems] = useState<WorkflowExecution[] | null>(null);
    const [total, setTotal] = useState(0);

    const load = useCallback(() => {
        getWorkflowExecutions({ page, maxResults: PER_PAGE, scenario: scenario || undefined, active: active ? 1 : undefined, status: !active && status ? status : undefined })
            .then((r: any) => {
                setItems(r.items || []);
                setTotal(r.meta?.totalCount || 0);
            })
            .catch(() => setItems([]));
    }, [page, scenario, active, status]);

    useEffect(() => load(), [load]);

    // Active flows change on their own: keep them fresh while the page is open.
    useEffect(() => {
        if (!active) {
            return;
        }
        const timer = setInterval(load, ACTIVE_REFRESH_MS);

        return () => clearInterval(timer);
    }, [active, load]);

    const cancel = (e: WorkflowExecution) =>
        cancelWorkflowExecution(e.uuid).then(() => { toast.success(t('workflow:cancelled')); load(); }).catch((err: any) => toast.error(err?.message || t('workflow:actionFailed')));

    const openRun = (e: WorkflowExecution) => navigate(route.AUTOMATION_RUN.replace(':uuid', e.uuid));

    const columns = [
        actionsColumn((e: WorkflowExecution) => (
            <RowActions actions={[
                { icon: 'ri-file-list-3-fill', label: t('workflow:runDetails'), onClick: () => openRun(e) },
                { icon: 'ri-flow-chart', label: t('workflow:showOnCanvas'), onClick: () => navigate(route.AUTOMATION_SCENARIO.replace(':uuid', e.scenario?.uuid || '') + '?run=' + e.uuid), hidden: !e.scenario?.uuid },
                'divider',
                { icon: 'ri-stop-circle-fill', label: t('workflow:cancelRun'), onClick: () => cancel(e), hidden: !(['queued', 'running', 'waiting'].includes(e.status) && e.actions?.canEdit), danger: true },
            ]} />
        )),
        {
            name: t('workflow:scenario'),
            grow: 3,
            cell: (e: WorkflowExecution) => (
                <span className="mw-0">
                    <span className="d-block fw-medium text-truncate">{e.scenario?.name}</span>
                    <span className="d-block text-muted fs-12">{t('workflow:origin_' + (e.origin?.type || 'other'), { defaultValue: e.origin?.type })} · v{e.scenarioVersion}</span>
                </span>
            ),
        },
        { name: t('workflow:status'), cell: (e: WorkflowExecution) => <ExecutionStatusBadge status={e.status} /> },
        {
            name: active ? t('workflow:waitingForColumn') : t('workflow:started'),
            grow: 2,
            cell: (e: WorkflowExecution) => active && e.waitingFor
                ? <span>{t('workflow:waitingFor_' + (e.waitingFor.type || 'other'), { defaultValue: e.waitingFor.type })}{e.waitingFor.until ? ' · ' + formatWorkflowDate(e.waitingFor.until) : ''}</span>
                : formatWorkflowDate(e.startedAt || e.createdAt),
        },
        { name: t('workflow:duration'), cell: (e: WorkflowExecution) => duration(e) },
        { name: t('workflow:operations'), cell: (e: WorkflowExecution) => e.operations },
    ];

    const STATUSES: ExecutionStatus[] = ['success', 'warning', 'error', 'cancelled'];

    const chip = (on: boolean) => 'btn btn-sm ' + (on ? 'btn-primary' : 'btn-light');

    return (
        <>
            <BreadCrumb title={t('menu:automationRuns')} pageTitle={t('menu:automation')} />
            <ListCard
                title={active ? t('workflow:activeFlows') : t('workflow:history')}
                headerExtra={<button type="button" className="btn btn-sm btn-soft-secondary" onClick={load}><i className="ri-refresh-line align-bottom" /></button>}
                filters={
                    <div className="d-flex flex-wrap align-items-center gap-2">
                        <button type="button" className={chip(!active)} onClick={() => { setActive(false); setPage(1); }}>{t('workflow:history')}</button>
                        <button type="button" className={chip(active)} onClick={() => { setActive(true); setPage(1); }}>{t('workflow:activeFlows')}</button>
                        {!active &&
                            <select className="form-select form-select-sm ms-2" style={{ width: 200 }} value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}>
                                <option value="">{t('workflow:allStatuses')}</option>
                                {STATUSES.map((s) => <option key={s} value={s}>{t('workflow:executionStatus_' + s)}</option>)}
                            </select>
                        }
                        {scenario && <Link to={route.AUTOMATION_RUNS} className="btn btn-sm btn-ghost-primary ms-auto"><i className="ri-close-line align-bottom me-1" />{t('workflow:allScenarios')}</Link>}
                    </div>
                }
            >
                {items === null ? <Loader /> :
                    items.length === 0
                        ? <div className="text-center text-muted py-5"><i className="ri-history-line display-5 d-block mb-2" /><p className="fs-14 mb-0">{active ? t('workflow:noActiveFlows') : t('workflow:noRuns')}</p></div>
                        : <WorkflowTable columns={columns as any} data={items} onRowClick={openRun} paging={{ page, perPage: PER_PAGE, total, onPage: setPage }} />
                }
            </ListCard>
        </>
    );
};

export default RunList;
